import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import type Vessel from '../../Models/VesselModel';
import type VesselType from '../../Models/VesselTypeModel';
import type Company from '../../Models/CompanyModel';
import { shipNewToVisualString } from '../../Utils/VesselFunctions';

type ExistingVesselInfoDialogProps = {
    controller: boolean,
    onClose: () => void,
    vessel: Vessel | null,
    vesselTypes: VesselType[],
    companies: Company[],
};

function ExistingVesselInfoDialog(props: Readonly<ExistingVesselInfoDialogProps>): JSX.Element {

    return (
        <Dialog 
            open={props.controller} 
            onClose={props.onClose}
        >
            <DialogTitle>{props.vessel?.vesselName ?? 'ERROR'}</DialogTitle>
            <DialogContent>
                {props.vessel === null
                    ? <></>
                    : (
                        <table>
                            <tbody>
                                <tr>
                                    <td style={{
                                        padding: '5px 15px 5px 5px',
                                        fontWeight: 'bold',
                                    }}>
                                        Vessel Name
                                    </td>
                                    <td>
                                        {props.vessel.vesselName}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{
                                        padding: '5px 15px 5px 5px',
                                        fontWeight: 'bold',
                                    }}>
                                        IMO Number
                                    </td>
                                    <td>
                                        {props.vessel.imoNumber}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{
                                        padding: '5px 15px 5px 5px',
                                        fontWeight: 'bold',
                                    }}>
                                        Vessel Code
                                    </td>
                                    <td>
                                        {props.vessel.vesselCode}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{
                                        padding: '5px 15px 5px 5px',
                                        fontWeight: 'bold',
                                    }}>
                                        Sort Number
                                    </td>
                                    <td>
                                        {props.vessel.sortNo}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{
                                        padding: '5px 15px 5px 5px',
                                        fontWeight: 'bold',
                                    }}>
                                        Company
                                    </td>
                                    <td>
                                        {props.companies.find((company: Company) => company.companyNo === props.vessel?.companyNo)?.name ?? ''}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{
                                        padding: '5px 15px 5px 5px',
                                        fontWeight: 'bold',
                                    }}>
                                        Vessel Type
                                    </td>
                                    <td>
                                        {props.vesselTypes.find((vesselType: VesselType) => vesselType.vesselTypeId === props.vessel?.vesselTypeId)?.vesselType ?? ''}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{
                                        padding: '5px 15px 5px 5px',
                                        fontWeight: 'bold',
                                    }}>
                                        Currency
                                    </td>
                                    <td>
                                        {props.vessel.currCode}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{
                                        padding: '5px 15px 5px 5px',
                                        fontWeight: 'bold',
                                    }}>
                                        Built Date
                                    </td>
                                    <td>
                                        {shipNewToVisualString(props.vessel.shipNew)}
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        colSpan={2}
                                        style={{
                                            fontWeight: 'bold',
                                            paddingTop: '20px',
                                            paddingBottom: '10px',
                                        }}
                                    >
                                        Include in the following applications:
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{
                                        padding: '5px 15px 5px 5px',
                                        fontWeight: 'bold',
                                    }}>
                                        Purchase
                                    </td>
                                    <td>
                                        {props.vessel.inPurchase ? 'Yes' : 'No'}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{
                                        padding: '5px 15px 5px 5px',
                                        fontWeight: 'bold',
                                    }}>
                                        Maintenance
                                    </td>
                                    <td>
                                        {props.vessel.inMaintenance ? 'Yes' : 'No'}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{
                                        padding: '5px 15px 5px 5px',
                                        fontWeight: 'bold',
                                    }}>
                                        Budget
                                    </td>
                                    <td>
                                        {props.vessel.inBudget ? 'Yes' : 'No'}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{
                                        padding: '5px 15px 5px 5px',
                                        fontWeight: 'bold',
                                    }}>
                                        Experience
                                    </td>
                                    <td>
                                        {props.vessel.inExperience ? 'Yes' : 'No'}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{
                                        padding: '5px 15px 5px 5px',
                                        fontWeight: 'bold',
                                    }}>
                                        Voyage
                                    </td>
                                    <td>
                                        {props.vessel.inVoyage ? 'Yes' : 'No'}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{
                                        padding: '5px 15px 5px 5px',
                                        fontWeight: 'bold',
                                    }}>
                                        Logbook
                                    </td>
                                    <td>
                                        {props.vessel.inLogbook ? 'Yes' : 'No'}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{
                                        padding: '5px 15px 5px 5px',
                                        fontWeight: 'bold',
                                    }}>
                                        Docking
                                    </td>
                                    <td>
                                        {props.vessel.inDocking ? 'Yes' : 'No'}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    )
                }
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={props.onClose}
                    color='error'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ExistingVesselInfoDialog;